import React from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import moment from 'moment';
import NumericLabel from 'react-pretty-numbers';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { dateMonthYearDateFormat } from '../../../constants';

const INVOICE_STATUS_LABELS = { 1: 'Draft', 2: 'Sent', 3: 'Paid', 4: 'Cancelled', 5: 'Partially-Paid', 6: 'Updated', 7: 'Approved' };

const EVENT_STYLES = {
    success: { border: '#85CA85', bg: '#e8f5e9', color: '#2e7d32' },
    failed: { border: '#f94e35', bg: '#fdecea', color: '#c62828' },
    deleted: { border: '#9ca3af', bg: '#f3f4f6', color: '#4b5563' },
};

// Payment progression of an invoice - a sync whose status sits lower than the previous sync's means the status was reverted in between.
const STATUS_PROGRESS_RANK = { 1: 0, 2: 1, 5: 2, 3: 3 };

const isStatusReverted = (item, previous) => {
    if (['event', 'qb_action'].some(key => String(item?.[key] || '').toLowerCase().includes('revert'))) return true;
    if (item?.payments_reversed?.length) return true;
    const currentRank = STATUS_PROGRESS_RANK[item?.invoice_status_at_sync];
    const previousRank = STATUS_PROGRESS_RANK[previous?.invoice_status_at_sync];
    return currentRank !== undefined && previousRank !== undefined && currentRank < previousRank;
};

const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const getEventKey = (item) => {
    const event = String(item?.event || '').toLowerCase();
    if (['fail', 'error'].some(s => event.includes(s))) return 'failed';
    if (item?.qb_invoice_deleted || item?.qb_action === 'deleted') return 'deleted';
    return 'success';
};

const PaymentRows = ({ title, payments, currency, reversed }) => (
    <Box sx={{ mt: 0.75 }}>
        {/* <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: reversed ? EVENT_STYLES.failed.color : '#374151', mb: 0.25 }}>{title} ({payments.length})</Typography> */}
        {payments.map((payment, index) => (
            <Box key={payment?.pp_id || index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, borderTop: '1px solid #f3f4f6' }}>
                <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: reversed ? EVENT_STYLES.failed.color : '#374151', textDecoration: reversed ? 'line-through' : 'none' }}>
                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: currency, precision: 2, commafy: true, justification: 'L' }}>{payment?.amount || 0}</NumericLabel>
                </Typography>
                {/* {payment?.type ? <Typography sx={{ fontSize: '0.65rem', color: '#6b7280' }}>{humanize(payment.type)}</Typography> : null} */}
                <Box sx={{ flex: 1 }} />
                {payment?.payment_date ? <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', flexShrink: 0 }}>{moment(payment.payment_date).format(dateMonthYearDateFormat)}</Typography> : null}
                {payment?.qb_payment_id ? <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af', flexShrink: 0 }}>QB #{payment.qb_payment_id}</Typography> : null}
            </Box>
        ))}
    </Box>
);

const getEventIcon = (eventKey) => {
    const s = { fontSize: 15 };
    if (eventKey === 'failed') return <ErrorOutlineIcon sx={s} />;
    if (eventKey === 'deleted') return <DeleteOutlineIcon sx={s} />;
    return <SyncIcon sx={s} />;
};

const QbSyncTimelineCard = ({ syncHistory = [], skeletonLoader, currency }) => {
    if (skeletonLoader) {
        return (
            <Box sx={{ px: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="rounded" width="80%" height={18} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="60%" height={14} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    if (!syncHistory?.length) {
        return (
            <Typography align="center" sx={{ py: 2, fontSize: '0.875rem', color: '#6b7280' }}>
                No Sync History Available
            </Typography>
        );
    }

    return (
        <Box sx={{ px: 0.5, pt: 0.5 }}>
            {syncHistory.map((item, index) => {
                const isLast = index === syncHistory.length - 1;
                const eventKey = getEventKey(item);
                const colors = EVENT_STYLES[eventKey];
                const statusAtSync = item?.invoice_status_at_sync;
                const paymentsPushed = item?.payments_pushed || [];
                const paymentsReversed = item?.payments_reversed || [];
                // Previous sync by time (not array position), so this works whichever order the list is passed in.
                const previousSync = syncHistory.filter(entry => moment(entry?.synced_at).isBefore(moment(item?.synced_at))).sort((a, b) => moment(b?.synced_at).diff(moment(a?.synced_at)))[0];
                const statusReverted = isStatusReverted(item, previousSync);

                return (
                    <Box key={item?.id || index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', position: 'relative', mb: isLast ? 0 : 0.75 }}>
                        {!isLast && (
                            <Box sx={{ position: 'absolute', left: '12px', top: '26px', bottom: '-6px', width: '2px', bgcolor: '#d1d5db', borderRadius: 1, zIndex: 0 }} />
                        )}
                        <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: colors.bg, color: colors.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {getEventIcon(eventKey)}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, border: '1px solid #e5e7eb', borderLeft: `3px solid ${colors.border}`, borderRadius: 1.5, p: '8px 10px', bgcolor: '#fff', minWidth: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{humanize(item?.event) || 'Synced'}</Typography>
                                    {item?.qb_action && item.qb_action !== item?.event ? (
                                        <Chip label={humanize(item.qb_action)} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: colors.bg, color: colors.color, '& .MuiChip-label': { px: 0.6 } }} />
                                    ) : null}
                                </Box>
                                {item?.synced_at && moment(item.synced_at).isValid() ? (
                                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.3, flexShrink: 0 }}>{moment(item.synced_at).format(dateMonthYearDateFormat)}</Typography>
                                ) : null}
                            </Box>
                            {item?.synced_by_info?.name ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                    <PersonOutlineIcon sx={{ fontSize: 12, color: '#9ca3af' }} />
                                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>{item.synced_by_info.name}</Typography>
                                </Box>
                            ) : null}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {statusAtSync ? (
                                    <Chip className={`invoice-status-${statusAtSync}`} label={`Status at Sync: ${INVOICE_STATUS_LABELS[statusAtSync] || statusAtSync}`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, '& .MuiChip-label': { px: 0.6 } }} />
                                ) : null}
                                {statusReverted ? (
                                    <Chip label="Status Reverted" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74', '& .MuiChip-label': { px: 0.6 } }} />
                                ) : null}
                                {item?.qb_invoice_deleted ? (
                                    <Chip label="Deleted in QB" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: EVENT_STYLES.failed.bg, color: EVENT_STYLES.failed.color, '& .MuiChip-label': { px: 0.6 } }} />
                                ) : null}
                            </Box>
                            {item?.qb_invoice_id ? (
                                <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', mt: 0.5 }}>
                                    QB {item?.qb_entity_type || 'Invoice'} ID: {item.qb_invoice_id}
                                </Typography>
                            ) : null}
                            {paymentsPushed.length ? <PaymentRows title="Payments Pushed" payments={paymentsPushed} currency={currency} /> : null}
                            {/* {paymentsReversed.length ? <PaymentRows title="Payments Reversed" payments={paymentsReversed} currency={currency} reversed /> : null} */}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default QbSyncTimelineCard;
